import { Box, Grid, Typography } from '@mui/material';
import * as yup from 'yup';
import { useQueryClient } from 'react-query';
import {
  SparkForm,
  SparkFormImageUploader,
  SparkTextField,
  SparkDropdown,
  SparkBasicFormFooter,
  SparkRadioGroup,
} from 'sharedComponents/SparkForm';
import { useIndustries } from 'hooks/BusinessIndustries/useIndustries';
import { useCountries } from 'hooks/Countries/useCountries';
import { getInitialStringValue } from 'utilities/form';
import { useUpdateCompanyOverview } from 'hooks/CompanyOverview/useUpdateCompanyOverview';
import { useUpdateWorkSpace } from 'hooks/Workspace/useUpdateWorkSpace';
import { CompanyOverview } from 'types/CompanyOverview';
import { Workspace } from 'types/Workspace';
import { useResize } from 'hooks/common';
import { PersonalOverview } from 'types/PersonalOverview/personalOverview';
import { useUpdatePersonalOverview } from 'hooks/PersonalOverview/useUpdatePersonalOverview';
import { DropdownOption } from 'types/common/DropdownOption';

type EditPersonalInfoFromSettingsValues = {
  name: string;
  countryID: string | Number;
  industryID: string | Number;
  dateofBirth: string;
  about: string;
  genderID: string;
};

const validationSchema = yup.object({
  name: yup.string().required('Required'),
  surname: yup.string().required('Required'),
  occupation: yup.string().required('Required'),
  countryID: yup.string().required('Required'),
  industryID: yup.string().required('Required'),
  dateofBirth: yup.string().required('Required'),
  about: yup.string().required('Required'),
  genderID: yup.string().required('Required'),
});

type EditPersonalOverviewFormProps = {
  personalOverview: PersonalOverview | undefined;
  workspace: Workspace | undefined;
  genderOptions: DropdownOption[];
};

const EditPersonalOverviewForm = ({
  personalOverview,
  workspace,
  genderOptions,
}: EditPersonalOverviewFormProps) => {
  const { isMobile } = useResize();
  const queryClient = useQueryClient();
  const { dropdownOptions: countries, isLoading: isLoadingCountries } =
    useCountries();
  const { dropdownOptions: industries, isLoading: isLoadingIndustries } =
    useIndustries();

  const { mutateAsync: updatePersonalOverview } = useUpdatePersonalOverview(
    personalOverview?.id,
    { shouldInvalidatedOnSuccess: false }
  );

  const initialValues = {
    name: getInitialStringValue(personalOverview?.attributes?.name),
    surname: getInitialStringValue(personalOverview?.attributes?.surname),
    occupation: getInitialStringValue(personalOverview?.attributes?.occupation),
    countryID: getInitialStringValue(personalOverview?.attributes?.countryID),
    industryID: getInitialStringValue(
      personalOverview?.attributes?.businessIndustryID
    ),
    dateofBirth: getInitialStringValue(
      personalOverview?.attributes?.dateofBirth
    ),
    about: getInitialStringValue(personalOverview?.attributes?.about),
    genderID: getInitialStringValue(personalOverview?.attributes?.genderID),
  };

  const handleSubmitCompanyInfo = async (
    values: EditPersonalInfoFromSettingsValues
  ) => {
    const { name, countryID, industryID, genderID, dateofBirth, about } =
      values || {};
    await updatePersonalOverview({
      name,
      countryID: +countryID,
      businessIndustryID: +industryID,
      dateofBirth,
      about,
      genderID: +genderID,
    });
    queryClient.invalidateQueries(['personalOverview', personalOverview?.id]);
  };

  return (
    <Grid container>
      <Typography
        variant='h5'
        sx={{
          fontFamily: 'Mont-semibold',
          marginTop: '20px',
          marginBottom: '20px',
        }}
      >
        Personal Info
      </Typography>
      <Box
        width='100%'
        bgcolor='white'
        borderRadius='20px'
        border={(theme) => `1px solid ${theme.palette.borderColor}`}
      >
        <SparkForm<EditPersonalInfoFromSettingsValues>
          initialValues={initialValues}
          validationSchema={validationSchema}
          enableReinitialize={true}
          onSave={handleSubmitCompanyInfo}
          containerProps={{ padding: 2, spacing: 2 }}
        >
          <SparkTextField
            name='name'
            label='Name'
            placeholder='Enter Your Name'
          />
          <SparkTextField
            name='surname'
            label='Surname'
            placeholder='Enter Your Surname'
          />

          <SparkTextField
            name='occupation'
            label='Occupation'
            placeholder='Enter Your Occupation'
          />

          <SparkDropdown
            name='industryID'
            label='Industry'
            children={[...industries]}
          />
          <SparkDropdown
            name='genderID'
            label='Gender'
            children={[...genderOptions]}
          />
          <SparkTextField
            name='dateofBirth'
            label='Date of Birth'
            type='date'
          />

          <SparkTextField
            name='about'
            label='About'
            placeholder='Tell us more about yourself'
            multiline
            rows={4}
            size={12}
          />

          <SparkBasicFormFooter
            shouldRenderResetButton={true}
            primaryButtonStyles={{ width: '200px' }}
            resetButtonStyles={{ width: '200px' }}
            resetButtonText='Cancel'
            shouldRequireFieldUpdates={true}
          />
        </SparkForm>
      </Box>
    </Grid>
  );
};

export default EditPersonalOverviewForm;
